import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { FileData, FileDataAction } from './useFileData'
import Parties from '../../pages/Parties'
import EmailOverview from '../../pages/Email/EmailOverview'
import DocumentOverview from '../../pages/Document/DocumentOverview'
import Times from '../../pages/Times'
import DisbursementOverview from '../../pages/File/Registration/DisbursementOverview'
import { LegalAidCertificateOverview } from '../../pages/File/Registration/LegalAidCertificateOverview'
import TravelOverview from '../../pages/File/Registration/TravelOverview'
import Notes from '../../pages/Notes'
import { useLocation } from 'react-router-dom'
import { DocketProceedingOverview } from '../../pages/File/Registration/DocketProceedingOverview'
import FamilyLawOverview from '../../pages/File/Registration/FamilyLawOverview'
import InjuryOverview from '../../pages/File/Registration/InjuryOverview'
import AttributesOverview from '../../pages/File/AttributesOverview'
import FinanceMonitoringOverview from '../../pages/File/FinanceMonitoringOverview'
import useAttributes, { AttributeContextType, AttributeDefinitionDto, AttributeViewType } from '../useAttributes'
import { useTranslation } from 'react-i18next'
import AuditTrailOverview from '../../pages/File/AuditTrailOverview'
import Tasks from '../../pages/Tasks'
import Workflows from '../../pages/Workflows'
import OldFixedPriceOverview from '../../pages/File/Registration/OldFixedPriceOverview'

export type FilePageProps = {
    key: string
    dataActionKey?: string
    pivotItemKey?: string // Determines the active item in the pivot menu.
    route: string
    content: (file: FileData, action: FileDataAction) => ReactElement
}

export const fileRoutePrefix = '/file/:fileId'

const defaultPageKey = 'document'

export default function useFilePages(fileId?: string): {
    pages: FilePageProps[]
    activePage: FilePageProps
    getPageRoute: (route: string) => string
} {
    const { attributeDefinitions } = useAttributes(AttributeViewType.View, [AttributeContextType.File])
    const location = useLocation()
    const { t: translate } = useTranslation()

    const groupedAttributeDefinitions = useMemo(() => {
        return attributeDefinitions?.reduce((previousValue, currentValue) => {
            const category = currentValue.category || translate('file.pivot.attribute.title')
            const prevValues = previousValue.get(category) || []
            previousValue.set(category, [...prevValues, currentValue])
            return previousValue
        }, new Map<string, AttributeDefinitionDto[]>())
    }, [attributeDefinitions])

    const attributePages = useMemo(() => {
        return groupedAttributeDefinitions
            ?.entries()
            ?.map(entry => {
                const key = (entry[0] || translate('file.pivot.attribute.title')).toLowerCase()

                return {
                    key: `attributes-${key}`,
                    route: `attribute-${key}`,
                    content: (file: FileData) => <AttributesOverview entity={file.current} category={entry[0]} attributeDefinitions={entry[1]} />,
                }
            })
            .filter(Boolean)
    }, [groupedAttributeDefinitions])

    const pages = useMemo<FilePageProps[]>(
        () => [
            {
                key: 'party',
                route: 'party',
                content: (file: FileData) => (
                    <Parties
                        prefix={fileRoutePrefix}
                        overviewProps={{
                            data: file.party.data,
                            documents: file.document.data,
                            file: file.current.data,
                        }}
                    />
                ),
            },
            {
                key: 'email',
                route: 'email',
                content: (file: FileData, action: FileDataAction) => (
                    <EmailOverview messages={file.email} documents={file.document} file={file.current} reset={action} />
                ),
            },
            {
                key: 'document',
                route: 'document',
                content: (file: FileData, action: FileDataAction) => (
                    <DocumentOverview documents={file.document} parties={file.party.data} file={file.current.data} reset={action} />
                ),
            },
            {
                key: 'task',
                route: 'task',
                content: (file: FileData) => <Tasks routePrefix={fileRoutePrefix} currentFile={file.current.data} />,
            },
            {
                key: 'workflow',
                route: 'workflow',
                content: (file: FileData) => <Workflows routePrefix={fileRoutePrefix} currentFile={file.current.data} />,
            },
            {
                key: 'time',
                pivotItemKey: 'registration',
                route: 'time',
                content: (file: FileData) => <Times routePrefix={fileRoutePrefix} currentFile={file.current.data} />,
            },
            {
                key: 'disbursement',
                pivotItemKey: 'registration',
                route: 'disbursement',
                content: (file: FileData, action: FileDataAction) => (
                    <DisbursementOverview listProps={{ file: file.current.data }} items={file.disbursement.data} reset={action} />
                ),
            },
            {
                key: 'fixedPrice',
                pivotItemKey: 'registration',
                route: 'fixed-price',
                content: (file: FileData, action: FileDataAction) => (
                    <OldFixedPriceOverview listProps={{ file: file.current.data }} items={file.fixedPrice.data} reset={action} />
                ),
            },
            {
                key: 'legalAidCertificate',
                pivotItemKey: 'registration',
                route: 'legal-aid-certificate',
                content: (file: FileData, action: FileDataAction) => (
                    <LegalAidCertificateOverview file={file.current} parties={file.party} certificates={file.legalAidCertificate} reset={action} />
                ),
            },
            {
                key: 'docketProceeding',
                pivotItemKey: 'registration',
                route: 'docket',
                content: (file: FileData, action: FileDataAction) => (
                    <DocketProceedingOverview file={file.current} parties={file.party} docketProceedings={file.docketProceeding} reset={action} />
                ),
            },
            {
                key: 'injury',
                pivotItemKey: 'registration',
                route: 'injury',
                content: () => <InjuryOverview />,
            },
            {
                key: 'travel',
                pivotItemKey: 'registration',
                route: 'travel',
                content: (file: FileData, action: FileDataAction) => (
                    <TravelOverview listProps={{ file: file.current.data }} items={file.travel.data} reset={action} />
                ),
            },
            {
                key: 'note',
                route: 'note',
                content: (file: FileData) => <Notes currentFile={file.current.data} routePrefix={fileRoutePrefix} />,
            },
            {
                key: 'personlaw',
                pivotItemKey: 'registration',
                route: 'person-law',
                content: (file: FileData) => <FamilyLawOverview file={file.current} />,
            },
            ...attributePages,
            {
                key: 'monitoring',
                route: 'monitoring',
                content: (file: FileData) => <FinanceMonitoringOverview entity={file.current} />,
            },
            {
                key: 'audittrail',
                route: 'audit-trail',
                content: (file: FileData) => <AuditTrailOverview entity={file.current} />,
            },
        ],
        [fileId, attributePages],
    )

    // By default, the item's key will be equal to the route, but it may be overridden by the route property.
    const getPageRoute = (route: string): string => (fileId && route ? `/file/${fileId}/${route}` : undefined)

    const activePage: FilePageProps = useMemo<FilePageProps>(() => {
        // The base route, e.g. /file/<UUID>/time
        const getBaseRoute = (page: FilePageProps) => getPageRoute(page.route)
        const activePageKey = pages.find(page => location.pathname.search(getBaseRoute(page)) === 0)?.key || defaultPageKey
        return pages.find(page => page.key === activePageKey)
    }, [location.pathname, fileId, attributePages])

    return { pages, activePage, getPageRoute }
}
